import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from '@michelin/central-provider';
import {
  Container,
  Fab,
  GlobalActions,
  LoadingBounce,
  Panel,
  PhoneInput,
  PreventTransitionPrompt,
  TextField,
  useDialogs,
} from '@michelin/acid-components';
import { cloneDeep } from 'lodash';
import {
  FormControlLabel, Grid, IconButton, InputAdornment, Paper, Switch,
} from '@material-ui/core';
import {
  ArrowBack, Close, Edit, Email, Save, Timelapse,
} from '@material-ui/icons';
import { getOptions, SelectOption } from '@michelin/select-options-provider';
import { useMutation, useQueryClient } from 'react-query';
import ListingFilterSelect from '@michelin/acid-components/build/components/ListingFilters/ListingFilterSelect';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import uuid from 'uuid';
import Select from '@michelin/acid-components/build/components/Select/Select';
import { useSnackbar } from 'notistack';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { PageTitle } from 'Utils/CanvasPageTitle';
import { PlainMessage } from 'components/PlainMessage';
import { HoursOfOperationView } from 'components/HoursOfOperation/View';
import { HoursOfOperationEdit } from 'components/HoursOfOperation/Edit';
import { hasEmailFormat, scrollToDisplayFirstError } from 'Utils';
import {
  ContactDetailsProps,
  hasContactTypePriorityLevels,
  hasContactTypePriorityLevelsAssigned,
  hasContactTypeRoleLevel,
  hasMichelinContactTypes,
  manageContactSave,
} from './Utils';

import {
  Contact,
  GlobalTextInputProps,
  MechanicCertification,
  MechanicOutsideCertification,
  NotificationSubscription,
  SPPContactTypeLevelsEnum,
  SPPContactTypeRoleAreasEnum,
  SPPContactTypeServicesEnum,
} from '../../Types';
import useCustomer from '../../hooks/useCustomer';
import { useContact } from '../../hooks/useContactQueries';
import { AssignContactTypesState } from './ContactTypes/utils';
import { PriorityLevelsManagementModal } from './ContactTypes/PriorityLevelsManagementModal';
import { MechanicCertificationsList } from './MechanicCertifications/List';
import { MechanicOutsideCertificationsList } from './MechanicOutsideCertifications/List';
import { NotificationSubscriptionsList } from './NotificationSubscriptions/List';
import { ContactTypesList } from './ContactTypes/List';

export function ContactDetail(props: ContactDetailsProps) {
  const permissions = usePermissionsSPP();
  const history = useHistory();
  const contactIdProps = props.contactId;
  const { stNumber, btNumber, contactId } = useParams<{ [key: string]: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const currentContactId = contactIdProps || contactId;

  const { errorDialog, confirmDialog } = useDialogs();
  const { t } = useTranslation();

  // Logic
  const [accountTypes, setAccountTypes] = useState<SelectOption[]>([] as SelectOption[]);
  const [forceReload, setForceReload] = useState<number>(Date.now());
  const [commercialMechanicContactFlag, setCommercialMechanicContactFlag] = useState<boolean>(false);
  const [commercialTireTechnicianContactFlag, setCommercialTireTechnicianContactFlag] = useState<boolean>(false);
  const [hasCertificatesFlag, setHasCertificatesFlag] = useState<boolean>(false);
  const [hasOutsideCertificatesFlag, setHasOutsideCertificatesFlag] = useState<boolean>(false);
  const [assigningContactTypes, setAssigningContactTypes] = useState<AssignContactTypesState | null>(null);

  const {
    control,
    handleSubmit,
    formState,
    trigger,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    watch,
  } = useForm<Contact>({ defaultValues: {} as Contact });

  const queryClient = useQueryClient();

  const customerQuery = useCustomer(stNumber);

  const contactQuery = useContact({
    stNumber,
    contactId: currentContactId,
    defaultData: props.action === 'add' ? props.defaultData || {} as Contact : undefined,
    queryKey: `contacts-detail-${forceReload.toString()}`,
  });

  const { mutateAsync } = useMutation(manageContactSave, { onSuccess: () => queryClient.invalidateQueries(['contact-detail', stNumber, currentContactId]) });

  const preferredComunication = watch('preferred_method');

  useEffect(() => {
    if (customerQuery.data && contactQuery.data) {
      if (!formState.isDirty) reset(cloneDeep(contactQuery.data));
      // Filter Account Types Filter by Location Hierarchy
      let accountTypesOptions = getOptions('account_types');
      accountTypesOptions = accountTypesOptions.filter((e) => customerQuery?.data && (
        (e.value === 'ST' && customerQuery.data.ship_to_customer && customerQuery.data.ship_to_customer !== 'null')
                || (e.value === 'BT' && customerQuery.data.bill_to_customer && customerQuery.data.bill_to_customer !== 'null')
                || (e.value === 'HO' && false)
                || (e.value === 'PC' && false)
                || (e.value === 'All')
      ));
      setAccountTypes(accountTypesOptions);
      setHasCertificatesFlag(!!contactQuery.data.certifications.length);
      setHasOutsideCertificatesFlag(!!contactQuery.data.outside_certifications.length);
      const commercialMechanicContact: boolean = hasContactTypeRoleLevel(
        {
          data: contactQuery.data,
          services: [SPPContactTypeServicesEnum.commercial],
          roleAreas: [SPPContactTypeRoleAreasEnum.mechanic],
        },
      );
      setCommercialMechanicContactFlag(commercialMechanicContact);
      const commercialTireTechnicianContact: boolean = hasContactTypeRoleLevel(
        {
          data: contactQuery.data,
          services: [SPPContactTypeServicesEnum.commercial],
          roleAreas: [SPPContactTypeRoleAreasEnum.tire_technician],
        },
      );
      setCommercialTireTechnicianContactFlag(commercialTireTechnicianContact);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerQuery.data, contactQuery.data]);

  useEffect(() => {
    if (props.action === 'view') reset(cloneDeep(contactQuery.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.action]);

  useEffect(() => {
    async function reload() {
      await contactQuery.refetch();
    }

    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReload]);

  if (permissions.isLoading) {
    return <LoadingBounce style={{ height: '70vh' }} />;
  }

  if (!permissions.allowsAction('contacts.read') && props.action === 'view') {
    history.push(`/${btNumber}/contacts/${stNumber}/list`);
  }

  if (!permissions.allowsAction('contacts.update') && props.action === 'edit') {
    history.push(`/${btNumber}/contacts/${stNumber}/view/${currentContactId}`);
  }

  if (!permissions.allowsAction('contacts.create') && props.action === 'add') {
    history.push(`/${btNumber}/contacts/${stNumber}/list`);
  }

  if (!['edit', 'view', 'add'].includes(props.action)) {
    return (
      <PlainMessage
        title={t('Internal Error')}
        messages={t('Invalid action {{actionName}}', { actionName: props.action })}
      />
    );
  }

  if (!getValues() || contactQuery.isLoading || contactQuery.isFetching || customerQuery.isLoading || !accountTypes.length) {
    return <LoadingBounce style={{ height: '70vh' }} />;
  }

  if (contactQuery.isError || customerQuery.isError) {
    return (
      <PlainMessage
        title={t('Database Error')}
        messages={t('Application could not load contact data.\nPlease try again later or contact support if the error persists').split('\n')}
      />
    );
  }

  if (contactQuery.data.status === 1) {
    return (
      <PlainMessage
        title={t('Error loading data')}
        messages={[t('The contact does not exist')]}
      />
    );
  }

  let title;

  if (props.action === 'add') title = t('Add a new contact');
  if (props.action === 'view' || props.action === 'edit') title = `${getValues('first_name') || ''} ${getValues('last_name') || ''}`;
  if (props.action === 'edit') title = `${title} ${t('(Edit)')}`;

  function checkRequiredFields(): boolean {
    // (WorkPhone or CellPhone) is required
    if ((!getValues('work_phone') || getValues('work_phone')!.length === 0) && (!getValues('cell_phone') || getValues('cell_phone')!.length === 0)) {
      setError('cell_phone', { type: 'required' });
      setError('work_phone', { type: 'required' });

      errorDialog(
        t('Please remember that at least a Work or Cell Phone is required.').split('\n'),
        t('Error saving contact data.'),
      );
      scrollToDisplayFirstError();
      return false;
    }

    // Require Cell Phone if it was Chosen as Preferred Communication
    if ((getValues('preferred_method') === 'text' || (getValues('preferred_method') === 'cell')) && (!getValues('cell_phone') || getValues('cell_phone')!.length === 0)) {
      errorDialog(
        t('Please enter a Cell Phone or change the Preferred Communication Method.').split('\n'),
        t('Error saving contact data.'),
      );
      setError('cell_phone', { type: 'required' });
      scrollToDisplayFirstError();
      return false;
    }

    // Require Work Phone if it was Chosen as Preferred Communication
    if (getValues('preferred_method') === 'work' && (!getValues('work_phone') || getValues('work_phone')!.length === 0)) {
      errorDialog(
        t('Please enter a Work Phone or change the Preferred Communication Method.').split('\n'),
        t('Error saving contact data.'),
      );
      setError('work_phone', { type: 'required' });
      scrollToDisplayFirstError();
      return false;
    }

    // At least one contact type is required if we are not on the basicFields form
    if (!props.basicFields && !hasContactTypePriorityLevels(getValues('contact_types'))) {
      errorDialog(
        t('At least one contact type is required in order to save contact data.').split('\n'),
        t('Error saving contact data.'),
      );
      scrollToDisplayFirstError();
      return false;
    }

    if (!JSON.stringify(getValues('work_hours')).includes('begin')) {
      errorDialog(
        t('Working Hours are required in order to save contact data.').split('\n'),
        t('Error saving contact data.'),
      );
      scrollToDisplayFirstError();
      return false;
    }

    // If a notification subscription has cellphone as a method, the cellphone is required
    const notificationSubscriptions: NotificationSubscription[] = getValues('notification_subscription') || [];
    if (notificationSubscriptions.length) {
      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (const index in notificationSubscriptions) {
        const subscription: NotificationSubscription = notificationSubscriptions[index];
        // eslint-disable-next-line no-restricted-syntax
        for (const i in subscription.method) {
          if (subscription.method[i] === 'text_cellphone' && (!getValues('cell_phone') || getValues('cell_phone')?.length === 0)) {
            errorDialog(
              t('Please enter a Cell Phone to be used as notification method.').split('\n'),
              t('Error saving contact data.'),
            );
            scrollToDisplayFirstError();
            return false;
          }
        }
      }
    }

    // Commercial Mechanic and Tire Technician are not compatible with BT contact level
    const commercialMechanicContact = hasContactTypeRoleLevel(
      {
        data: getValues(),
        services: [SPPContactTypeServicesEnum.commercial],
        roleAreas: [SPPContactTypeRoleAreasEnum.mechanic],
      },
    );
    const commercialTireTechnicianContact = hasContactTypeRoleLevel(
      {
        data: getValues(),
        services: [SPPContactTypeServicesEnum.commercial],
        roleAreas: [SPPContactTypeRoleAreasEnum.tire_technician],
      },
    );
    if (getValues('contact_level') === 'BT' && (commercialMechanicContact || commercialTireTechnicianContact)) {
      errorDialog(
        t('Assignment of contact types Commercial Mechanic and Commercial Tire Technician, only apply to ST level').split('\n'),
        t('Error saving contact data.'),
      );
      scrollToDisplayFirstError();
      return false;
    }

    return true;
  }

  function applyContactLevelChange(newLevel: string): void {
    const cloneData = cloneDeep(getValues());
    cloneData.contact_level = newLevel;

    if (cloneData.contact_types) {
      const contact_types = cloneDeep(getValues('contact_types'));

      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (const i in contact_types) {
        // @ts-ignore
        if (!contact_types[i].id) contact_types[i].id = uuid.v4();
        // @ts-ignore
        // eslint-disable-next-line no-restricted-syntax
        for (const j in contact_types[i].role_areas) {
          // @ts-ignore
          if (contact_types[i].role_areas[j].levels || !contact_types[i].role_areas[j].levels.length) {
            // @ts-ignore
            // eslint-disable-next-line no-restricted-syntax
            for (const k in contact_types[i].role_areas[j].levels) {
              // @ts-ignore
              if (contact_types[i].role_areas[j].levels[k].location) {
                // Set all levels as general
                // @ts-ignore
                contact_types[i].role_areas[j].levels[k].level = SPPContactTypeLevelsEnum.general;
              }
            }
          }
        }
      }

      cloneData.contact_types = contact_types;
    }
    setValue('contact_types', cloneData.contact_types);
  }

  function updateContactLevel(event: React.ChangeEvent<any> | null) {
    const contact: Contact = cloneDeep(getValues());
    if (event) {
      if (props.action === 'add') {
        applyContactLevelChange(event.target.value);
        return;
      }
      if (contactQuery.data && event.target.value !== contactQuery.data.contact_level && contact.contact_types && contact.contact_types.length) {
        if (hasMichelinContactTypes(getValues('contact_types'), true).length) {
          confirmDialog(
            t('One or more contact types assigned to {{contactFirstName}} {{contactLastName}} '
              + 'have priority levels which must be reassigned to another contact at the location '
              + 'in order to complete this change. How do you wish to proceed?',
            { contactFirstName: getValues('first_name'), contactLastName: getValues('last_name') }),
            t('Reassign priority levels and contact types'),
            t('Reassign'),
            t('Undo'),
            () => {
              setAssigningContactTypes({
                contact: getValues(),
                action: 'reassign',
                force: true,
              });
            },
            undefined,
          );
        } else if (hasContactTypePriorityLevelsAssigned(getValues('contact_types'))) {
          confirmDialog(
            // eslint-disable-next-line max-len
            t('One or more contact types assigned to {{contactFirstName}} {{contactLastName}} have priority levels which can be reassigned to another contact at the location. How do you wish to proceed?',
              { contactFirstName: getValues('first_name'), contactLastName: getValues('last_name') }),
            t('Reassign priority levels and contact types'),
            t('Reassign'),
            t('Just change contact level'),
            () => {
              setAssigningContactTypes({
                contact: getValues(),
                action: 'reassign',
                force: false,
              });
            },
            () => {
              applyContactLevelChange(event.target.value);
            },
          );
        } else {
          applyContactLevelChange(event.target.value);
        }
      } else {
        applyContactLevelChange(event.target.value);
      }
    }
  }

  /// //////////////////////////////////////////////////////////////////
  /// SAVE METHODS
  /// //////////////////////////////////////////////////////////////////

  const saveContact: SubmitHandler<Contact> = async (data) => {
    const requiredFields = checkRequiredFields();
    if (!requiredFields) return;

    const response = await mutateAsync({
      locationNumber: stNumber,
      contact: data,
      contactId: currentContactId,
      basicFields: props.basicFields || false,
    });
    if (!response.status) {
      reset(response.data);
      if (props.onSave) {
        props.onSave(response.data);
        return;
      }

      if (props.action === 'add') {
        confirmDialog(
          t('Contact information for {{contactFirstName}} {{contactLastName}} has been saved. Do you want to set contact type priority levels at this location for this contact?',
            { contactFirstName: response.data.first_name, contactLastName: response.data.last_name }),
          t('Set contact type priority levels'),
          t('Yes'),
          t('No'),
          () => {
            setAssigningContactTypes({
              contact: response.data,
              action: 'assign',
              force: false,
            });
          },
          () => {
            history.push(`/${btNumber}/contacts/${stNumber}/view/${response.data.id}`);
            enqueueSnackbar(t('Contact information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
          },
          { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
        );
        return;
      }

      history.push(`/${btNumber}/contacts/${stNumber}/view/${response.data.id}`);
      enqueueSnackbar(t('Contact information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } else {
      errorDialog(response.message, response.message);
    }
  };

  function onFormValidationError(errors: any) {
    if (errors.email_address?.type === 'format') {
      errorDialog(
        t('Please check for wrong email format').split('\n'),
        t('Error checking general info data.'),
      );
      return;
    }
    errorDialog(
      t('Please check the marked fields.').split('\n'),
      t('Error saving contact data.'),
    );
    scrollToDisplayFirstError();
  }

  /// //////////////////////////////////////////////////////////////////
  /// RENDER METHODS
  /// //////////////////////////////////////////////////////////////////

  function renderContactGeneralInfo() {
    const readOnly: boolean = props.action === 'view';

    const textInputProps: GlobalTextInputProps = {
      variant: 'standard',
      InputProps: { readOnly },
      style: { display: 'flex', flexWrap: 'wrap' },
    };

    const defaultText: string = '';

    return (
      <Panel title={t('General Info')}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="contact_level"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      id="contact_level"
                      value={value || defaultText}
                      variant="standard"
                      label={t('Contact Level')}
                      required
                      options={accountTypes}
                      readOnly={readOnly || props.basicFields}
                      onChange={(e: any) => {
                        onChange(e.target.value);
                        trigger('contact_level');
                        updateContactLevel(e);
                      }}
                      onClose={() => {
                        trigger('contact_level');
                      }}
                      style={{ width: '100%' }}
                      error={!!formState.errors.contact_level}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="first_name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...textInputProps}
                      id="first_name"
                      label={t('First Name')}
                      value={value || defaultText}
                      onChange={onChange}
                      error={!!formState.errors.first_name}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="last_name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...textInputProps}
                      value={value || defaultText}
                      onChange={onChange}
                      error={!!formState.errors.last_name}
                      id="last_name"
                      label={t('Last Name')}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="job_title"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...textInputProps}
                      value={value || defaultText}
                      id="job_title"
                      label={t('Job Title')}
                      onChange={onChange}
                      error={!!formState.errors.job_title}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Controller
                  name="cell_phone"
                  control={control}
                  rules={{
                    validate: {
                      preferred: (value) => !(preferredComunication === 'cell' && !value),
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <PhoneInput
                      id="cell_phone"
                      label={t('Cell Phone Number')}
                      value={value || defaultText}
                      defaultCountry={(permissions.location?.customer_country === 'ca' ? 'CA' : 'US')}
                      readOnly={readOnly}
                      onChange={(target: any) => {
                        onChange(target.value);
                        trigger('cell_phone');
                        clearErrors('work_phone');
                      }}
                      required={!!formState.errors.cell_phone}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={9} md={5}>
                <Controller
                  name="work_phone"
                  control={control}
                  rules={{
                    validate: {
                      preferred: (value) => !(preferredComunication === 'work' && !value),
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <PhoneInput
                      id="work_phone"
                      label={t('Work Phone Number')}
                      value={value || defaultText}
                      defaultCountry={(permissions.location?.customer_country === 'ca' ? 'CA' : 'US')}
                      readOnly={readOnly}
                      onChange={!readOnly ? (target: any) => {
                        onChange(target.value);
                        trigger('work_phone');
                        clearErrors('cell_phone');
                      }
                        : undefined}
                      required={!!formState.errors.work_phone}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3} md={2}>
                <Controller
                  name="ext"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      type="number"
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...textInputProps}
                      value={value || defaultText}
                      onChange={onChange}
                      id="ext"
                      label={t('Ext.')}
                      inputProps={{ min: 0 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item xs={12}>
                    <Controller
                      name="fax"
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          id="fax"
                          label={t('Fax Number')}
                          value={value || defaultText}
                          defaultCountry={(permissions.location?.customer_country === 'ca' ? 'CA' : 'US')}
                          readOnly={readOnly}
                          onChange={!readOnly ? (target: any) => {
                            onChange(target.value);
                            trigger('fax');
                          } : undefined}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item xs={12}>
                    <Controller
                      name="email_address"
                      control={control}
                      rules={{
                        validate: {
                          format: (email) => hasEmailFormat(email),
                          preferred: (value) => !(preferredComunication === 'email' && !value),
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...textInputProps}
                          value={value || defaultText}
                          onChange={onChange}
                          error={!!formState.errors.email_address}
                          id="email_address"
                          label={t('Email')}
                          required
                          InputProps={{
                            ...textInputProps.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <IconButton
                                  aria-label={t('Email')}
                                  disabled
                                >
                                  <Email />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6} md={6}>
                <Controller
                  name="preferred_language"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <ListingFilterSelect
                      id="preferred_language"
                      value={value || defaultText}
                      variant="standard"
                      title={t('Preferred Language')}
                      required
                      options={getOptions('preferred_languages')}
                      readOnly={readOnly}
                      onChange={(e:any) => { onChange(e.target.value); trigger('preferred_language'); }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <Controller
                  name="preferred_method"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <ListingFilterSelect
                      id="preferred_method"
                      value={value || defaultText}
                      variant="standard"
                      title={t('Preferred Comunication')}
                      required
                      options={getOptions('preferred_communications')}
                      readOnly={readOnly}
                      onChange={(e:any) => {
                        onChange(e.target.value);
                        trigger('preferred_method');
                        clearErrors(['work_phone', 'cell_phone']);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} style={{ padding: 17 }}>
                <Controller
                  name="group"
                  defaultValue=""
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={value}
                          id="group"
                          readOnly={readOnly}
                          onChange={(!readOnly) ? (e:any) => {
                            onChange(e.target.checked);
                            trigger('group');
                          } : () => { }}
                          color="primary"
                        />
                      )}
                      label={t('Group/Shared')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} style={{ padding: 17 }}>
                <Controller
                  name="visible_externally"
                  defaultValue=""
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={value}
                          id="visible_externally"
                          readOnly={readOnly}
                          onChange={(!readOnly) ? (e:any) => {
                            onChange(e.target.checked);
                            trigger('visible_externally');
                          } : () => { }}
                          color="primary"
                        />
                      )}
                      label={t('Visible to Fleet')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Panel>
    );
  }

  function renderContactTypes() {
    if (props.basicFields) return <></>;
    const contact: any = getValues() || {} as Contact;

    return (
      <Controller
        name="contact_types"
        control={control}
        render={() => (
          <ContactTypesList
            contact={contact}
            stNumber={stNumber}
            btNumber={btNumber}
            action={props.action}
            countryCode={props.countryCode}
            hasCertificates={hasCertificatesFlag}
            hasOutsideCertificates={hasOutsideCertificatesFlag}
            onChange={(returnValue: any) => {
              setValue('contact_types', returnValue, { shouldDirty: true });
              contact.contact_types = returnValue;
              const commercialMechanicContact: boolean = hasContactTypeRoleLevel(
                {
                  data: getValues(),
                  services: [SPPContactTypeServicesEnum.commercial],
                  roleAreas: [SPPContactTypeRoleAreasEnum.mechanic],
                },
              );
              setCommercialMechanicContactFlag(commercialMechanicContact);
              const commercialTireTechnicianContact: boolean = hasContactTypeRoleLevel(
                {
                  data: getValues(),
                  services: [SPPContactTypeServicesEnum.commercial],
                  roleAreas: [SPPContactTypeRoleAreasEnum.tire_technician],
                },
              );
              setCommercialTireTechnicianContactFlag(commercialTireTechnicianContact);
              if (!commercialMechanicContact && !commercialTireTechnicianContact) contact.certifications = []; setValue('certifications', []);
              if (!commercialMechanicContact) contact.outside_certifications = []; setValue('outside_certifications', []);
            }}
            afterSave={() => {
              setAssigningContactTypes(null);
              setForceReload(Date.now());
            }}
            readOnly={false}
          />
        )}
      />
    );
  }

  function renderMechanicCertifications() {
    if (props.basicFields || (!commercialTireTechnicianContactFlag && !commercialMechanicContactFlag)) return <></>;

    const contact: any = getValues() || {} as Contact;

    const readOnly: boolean = props.action === 'view';

    return (
      <Controller
        name="certifications"
        control={control}
        render={() => (
          <MechanicCertificationsList
            contact={contact}
            readOnly={readOnly}
            onChange={(certifications: MechanicCertification[]) => {
              contact.certifications = certifications;
              setValue('certifications', certifications, { shouldDirty: true });
              setHasCertificatesFlag(certifications.length > 0);
            }}
          />
        )}
      />
    );
  }

  function renderMechanicOutsideCertifications() {
    if (props.basicFields || !commercialMechanicContactFlag) return <></>;

    const contact: any = getValues() || {} as Contact;

    const readOnly: boolean = props.action === 'view';

    return (
      <Controller
        name="outside_certifications"
        control={control}
        render={() => (
          <MechanicOutsideCertificationsList
            contact={contact}
            readOnly={readOnly}
            onChange={(certifications: MechanicOutsideCertification[]) => {
              contact.outside_certifications = certifications;
              setValue('outside_certifications', certifications, { shouldDirty: true });
              setHasOutsideCertificatesFlag(certifications.length > 0);
            }}
          />
        )}
      />
    );
  }

  function renderNotificationSubscriptions() {
    if (props.basicFields) return <></>;
    const contact: any = getValues() || {} as Contact;

    return (
      <Controller
        name="notification_subscription"
        control={control}
        render={() => (
          <NotificationSubscriptionsList
            contact={contact}
            action={props.action}
            onChange={(returnValue: any) => {
              contact.notification_subscription = returnValue;
              setValue('notification_subscription', returnValue, { shouldDirty: true });
              // onChange?.(null);
            }}
            readOnly={false}
          />
        )}
      />
    );
  }

  function renderContactWorkingHours() {
    const contact: any = getValues() || {} as Contact;
    const readOnly: boolean = props.action === 'view';

    return (
      <Panel title={t('Working Hours')}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {readOnly
              ? <HoursOfOperationView hoursOfOperations={contact.work_hours} mode="contact" />
              : (
                <Controller
                  name="work_hours"
                  control={control}
                  render={() => (
                    <HoursOfOperationEdit
                      hoursOfOperations={contact.work_hours}
                      mode="contact"
                      holidays={permissions.location?.customer_country || 'us'}
                      onChange={(returnData) => {
                        setValue('work_hours', returnData, { shouldDirty: true });
                        contact.work_hours = returnData;
                      }}
                    />
                  )}
                />
              )}
          </Grid>
        </Grid>
      </Panel>
    );
  }

  function renderActionButtons() {
    const adding: boolean = props.action === 'add';
    const viewing: boolean = props.action === 'view';
    const editing: boolean = (permissions.allowsAction('contacts.update') && viewing);

    return (
      <GlobalActions>
        <Fab
          color="primary"
          aria-label={t('Edit')}
          onClick={() => history.push(`/${btNumber}/contacts/${stNumber}/edit/${currentContactId}`)}
          disabled={!editing}
        >
          <Edit />
        </Fab>
        <Fab
          color="inherit"
          aria-label={t('Saving')}
          disabled={!formState.isSubmitting}
        >
          <Timelapse />
        </Fab>
        <Fab
          color={formState.isDirty ? 'primary' : 'inherit'}
          aria-label={t('Save')}
          onClick={formState.isDirty ? handleSubmit(saveContact, onFormValidationError) : undefined}
          disabled={viewing || formState.isSubmitting}
        >
          <Save />
        </Fab>
        <Fab
          color="primary"
          aria-label={t('Back')}
          onClick={() => {
            if (adding || viewing) {
              history.push(`/${btNumber}/contacts/${stNumber}/list`);
            } else {
              history.push(`/${btNumber}/contacts/${stNumber}/view/${currentContactId}`);
            }
          }}
          disabled={formState.isSubmitting || !!props.onClose}
        >
          <ArrowBack />
        </Fab>
        <Fab
          color="primary"
          aria-label={t('Close')}
          onClick={() => {
            props.onClose?.();
          }}
          disabled={!props.onClose}
        >
          <Close />
        </Fab>
      </GlobalActions>
    );
  }

  function renderContactForm() {
    return (

      <form>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8} style={{ marginBottom: 16 }}>
            {renderContactGeneralInfo()}
          </Grid>
          <Grid item xs={12} md={4} style={{ marginBottom: 16 }}>
            {renderContactWorkingHours()}
          </Grid>
          <Grid item xs={12} style={{ marginBottom: 16 }}>
            {renderContactTypes()}
          </Grid>
          {commercialTireTechnicianContactFlag || commercialMechanicContactFlag
            ? (
              <Grid item xs={12} style={{ marginBottom: 16 }}>
                {renderMechanicCertifications()}
              </Grid>
            )
            : null}
          {commercialMechanicContactFlag
            ? (
              <Grid item xs={12} style={{ marginBottom: 16 }}>
                {renderMechanicOutsideCertifications()}
              </Grid>
            )
            : null}
          <Grid item xs={12} style={{ marginBottom: 16 }}>
            {renderNotificationSubscriptions()}
          </Grid>
          {renderActionButtons()}
        </Grid>
      </form>
    );
  }

  if (props.basicFields) return renderContactForm();

  return (
    <>
      <PageTitle title={title} />
      <Container>
        <Paper>
          <Panel>
            {assigningContactTypes
              ? (
                <PriorityLevelsManagementModal
                  contactId={assigningContactTypes.contact.id}
                  contactLevel={assigningContactTypes.contact.contact_level}
                  firstName={assigningContactTypes.contact.first_name}
                  lastName={assigningContactTypes.contact.last_name}
                  stNumber={stNumber}
                  btNumber={btNumber}
                  countryCode={customerQuery.data.customer_country}
                  action={assigningContactTypes.action}
                  forceReassign={assigningContactTypes.force}
                  onClose={() => {
                    setAssigningContactTypes(null);
                  }}
                  onSave={() => {
                    if (assigningContactTypes.action === 'reassign') {
                      const contactLevel = (getValues('contact_level') === 'ST') ? 'BT' : 'ST';
                      applyContactLevelChange(contactLevel);
                    }
                    setAssigningContactTypes(null);
                    if (props.action === 'add') {
                      history.push(`/${btNumber}/contacts/${stNumber}/view/${getValues('id')}`);
                      // setForceReload(Date.now());
                    }
                  }}
                />
              )
              : null}
            {renderContactForm()}
          </Panel>
        </Paper>
      </Container>
      <PreventTransitionPrompt
        when={formState.isDirty && !formState.isSubmitSuccessful}
      />
    </>
  );
}
